import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';

import SyncIcon from '@material-ui/icons/Sync';
import {
  TEACHERS_BULK_UPLOAD_MATCH_STUDENTS_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
} from 'config/urls';
import { get } from 'lodash';
import {
  bulkUploadAutomatedMatchingStop,
  finishBulkUpload,
  useBulkUploadDetail,
  useBulkUploadGroups,
  useBulkUploadSubmissionsCount
} from 'sdk';

import GroupsList from 'pages/Teachers/BulkUpload/MatchStudents/components/GroupsList';
import Layout from 'pages/Teachers/BulkUpload/components/Layout';
import { useBulkUploadStatusRedirect } from 'pages/Teachers/BulkUpload/hooks';
import { useNavbar } from 'pages/Teachers/shared';
import colors from 'theme/palette';
import { useDialog } from 'utils/hooks';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { notifyErrors } from 'utils/notifications';
import { reverse, stringifyParams } from 'utils/urls';

import Button from 'components/Button';
import ConfirmDialog from 'components/ConfirmDialog';
import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import confettiImageSource from './assets/success.gif';
import styles from './styles.module.scss';

const MatchStudents = ({ match }) => {
  const user = useContext(UserContext);
  const [showLoader, setShowLoader] = useState(false);
  const [confettiAlreadyShown, setConfettiAlreadyShown] = useState(false);
  const [timePageStartsLoading] = useState(Date.now());

  const [stoppedTheMatching, setStoppedTheMatching] = useState(false);

  const history = useHistory();

  useNavbar({ title: 'CAPTURE WORK' });

  const {
    isOpened: isConfirmDialogOpened,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog
  } = useDialog();

  const bulkUploadId = get(match, 'params.bulkUploadId');

  const {
    data: unmatchedBulkUploadImageGroups,
    isLoading: isLoadingUnmatchedGroups,
    refetch: refetchUnmatchedImageGroups
  } = useBulkUploadGroups({
    params: { matched: false },
    bulkUploadId
  });

  const {
    data: matchedBulkUploadImageGroups,
    refetch: refetchMatchedImageGroups
  } = useBulkUploadGroups({
    params: { matched: true },
    bulkUploadId
  });

  const hasUnmatchedImages = useMemo(() => {
    return (
      !isLoadingUnmatchedGroups && unmatchedBulkUploadImageGroups?.length !== 0
    );
  }, [isLoadingUnmatchedGroups, unmatchedBulkUploadImageGroups]);

  const { data, refetch: refetchBulkUploadSubmissionsCount } =
    useBulkUploadSubmissionsCount({ bulkUploadId });

  const assignmentsCount = useMemo(() => {
    if (data) {
      return Object.keys(data).length;
    }
  }, [data]);

  useEffect(() => {
    if (assignmentsCount && assignmentsCount !== 0) {
      if (assignmentsCount === 1) {
        const sectionId = Object.keys(data)[0];
        const sectionData = data[sectionId];

        const pathname = reverse(TEACHER_ASSIGNMENT_V2_INSIGHTS_URL, {
          trackerInstanceSectionId: sectionId
        });

        const search =
          sectionData.has_math_standards &&
          stringifyParams({
            section: sectionId
          });

        history.push({
          pathname: pathname,
          search: search,
          state: { from: TEACHERS_BULK_UPLOAD_MATCH_STUDENTS_URL }
        });
      } else {
        const pathname = TEACHER_ASSIGNMENTS_V2_URL;
        history.push(pathname);
      }
    }
  }, [assignmentsCount, data, history]);

  const {
    data: bulkUpload,
    isLoading: isFetchingBulkUpload,
    refetch: refetchBulkUpload
  } = useBulkUploadDetail({ bulkUploadId });

  useBulkUploadStatusRedirect({
    bulkUpload,
    isLoading: isFetchingBulkUpload
  });

  const bulkUploadHasUnprocessedImages = get(
    bulkUpload,
    'has_unprocessed_images',
    true
  );
  const allBulkUploadImagesHaveBeenCreated = get(
    bulkUpload,
    'bulk_upload_images_have_been_successfully_created',
    false
  );
  const bulkUploadHasUngroupedImages = get(
    bulkUpload,
    'has_ungrouped_images',
    true
  );

  const shouldPoll = useMemo(() => {
    return (
      bulkUploadHasUnprocessedImages ||
      !allBulkUploadImagesHaveBeenCreated ||
      bulkUploadHasUngroupedImages
    );
  }, [
    bulkUploadHasUnprocessedImages,
    allBulkUploadImagesHaveBeenCreated,
    bulkUploadHasUngroupedImages
  ]);

  useEffect(() => {
    const delay = 2000;

    const timeoutId = setTimeout(() => {
      if (!hasUnmatchedImages && !shouldPoll && !confettiAlreadyShown) {
        setConfettiAlreadyShown(true);
      }
    }, delay);

    // Cleanup timeout if component unmounts
    return () => clearTimeout(timeoutId);
  }, [hasUnmatchedImages, shouldPoll, confettiAlreadyShown]);

  useEffect(() => {
    if (shouldPoll) {
      const interval = setInterval(() => refetchBulkUpload(), 2000);

      return () => clearInterval(interval);
    }
  }, [shouldPoll, refetchBulkUpload]);

  const trackTimePassedUntilMatchingFinished = useCallback(
    ({ timePassed }) => {
      trackMixpanelEvent(
        user.id,
        `${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`,
        {
          feature: 'capture',
          view: 'confirm',
          form: 'matching time',
          timePassed: timePassed,
          normalizedURL:
            process.env.REACT_APP_BASE_FRONTEND_URL +
            TEACHERS_BULK_UPLOAD_MATCH_STUDENTS_URL
        }
      );
    },
    [user.id]
  );

  useEffect(() => {
    if (!shouldPoll) {
      const timePageEndsLoading = Date.now();

      const secondsPassed =
        (timePageEndsLoading - timePageStartsLoading) / 1000;

      trackTimePassedUntilMatchingFinished({ timePassed: secondsPassed });
    }
  }, [shouldPoll, timePageStartsLoading, trackTimePassedUntilMatchingFinished]);

  const handleBulkUploadFinish = async () => {
    if (shouldPoll) {
      return;
    }
    setShowLoader(true);

    const { success, errors } = await finishBulkUpload({
      bulkUploadId
    });

    if (success) {
      refetchBulkUploadSubmissionsCount();
    } else {
      notifyErrors(errors);
    }

    setShowLoader(false);
  };

  const handleNextClick = () => {
    if (hasUnmatchedImages) {
      openConfirmDialog();
    } else {
      handleBulkUploadFinish();
    }
  };

  const handleStopMatching = async () => {
    setStoppedTheMatching(true);
    const { success, errors } = await bulkUploadAutomatedMatchingStop({
      bulkUploadId
    });

    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'stop matching',
        view: 'bulk upload',
        button: 'stop matching'
      }
    );

    if (!success) {
      notifyErrors(errors);
      setStoppedTheMatching(false);
    }
  };

  const assignmentId = bulkUpload?.assignment?.id;

  return (
    <Layout
      currentStepIndex={2}
      nextButtonAction={handleNextClick}
      isNextButtonDisabled={showLoader || shouldPoll || hasUnmatchedImages}
      nextButtonTooltip={
        hasUnmatchedImages
          ? 'You should match all images to students'
          : undefined
      }
      showLoader={showLoader}
      className={styles.wrapper}
      showDoneButton={true}
    >
      <div>
        {shouldPoll && (
          <div className={styles.matchingStudents}>
            <SyncIcon className={styles.syncIcon} />
            <Typography variant="H-TEXT-1" className={styles.matchingHeader}>
              Matching students
            </Typography>
            <Button
              className={styles.stopMatchingButton}
              onClick={handleStopMatching}
              color="pink"
              disabled={
                !allBulkUploadImagesHaveBeenCreated ||
                bulkUploadHasUngroupedImages ||
                stoppedTheMatching
              }
            >
              Stop Matching
            </Button>
          </div>
        )}
        {hasUnmatchedImages && (
          <div className={styles.subTitle}>
            <Typography variant="B-TEXT-1" className={styles.matchingSubheader}>
              Almost there! Confirm student names to unlock work insights.
            </Typography>
          </div>
        )}
      </div>

      {!hasUnmatchedImages && !shouldPoll && !confettiAlreadyShown && (
        <img
          src={`${confettiImageSource}?rand=${Math.random()}`}
          className={styles.confetti}
          alt="confetti"
        />
      )}

      <GroupsList
        bulkUploadId={bulkUploadId}
        assignmentId={assignmentId}
        shouldPoll={shouldPoll}
        unmatchedBulkUploadImageGroups={unmatchedBulkUploadImageGroups}
        refetchUnmatchedImageGroups={refetchUnmatchedImageGroups}
        matchedBulkUploadImageGroups={matchedBulkUploadImageGroups}
        refetchMatchedImageGroups={refetchMatchedImageGroups}
      />
      {isConfirmDialogOpened && (
        <ConfirmDialog
          onClose={closeConfirmDialog}
          onConfirm={() => {
            closeConfirmDialog();
            handleBulkUploadFinish();
          }}
        >
          <div>
            <Typography variant="B-Text-1" color={colors.grey1} align="center">
              All unmatched images will be permanently lost.
            </Typography>
          </div>
        </ConfirmDialog>
      )}
    </Layout>
  );
};

export default MatchStudents;
